import Accordion from "@material-ui/core/Accordion"
import AccordionSummary from "@material-ui/core/AccordionSummary"
import AccordionDetails from "@material-ui/core/AccordionDetails"
import Box from "@material-ui/core/Box"
import CircularProgress from "@material-ui/core/CircularProgress"
import Typography from "@material-ui/core/Typography"
import ReminderList from "../../reminders/components/ReminderList"
import { BaseNewReminderData, useGetRemindersQuery } from "../../reminders/api"
import { CalendarEventViewModel } from "../api/calendar-event"
import { useI18n } from "../../hooks"
import { guessReminderTime } from "../../reminders/utils/guess-reminder-time"
import { useReminderMutations } from "../../reminders/hooks/use-reminder-mutations"
import { isDate } from "lodash"
import { ReminderViewModel } from "../../reminders/api/reminder"
import { makeStyles, Theme } from "@material-ui/core"
import { ChevronDown } from "react-feather"

const queryOptions = { refetchOnMountOrArgChange: true } as const

const CalendarEventReminders = ({ calendarEvent }: CalendarEventRemindersProps) => {
  const { data: reminders = [], isLoading } = useGetRemindersQuery({ calendarEvent: calendarEvent.id }, queryOptions)
  const refStartDate = new Date(calendarEvent.startDate)
  const refEndDate = new Date(calendarEvent.endDate)
  const translations = useTranslations()
  const { createReminder } = useReminderMutations()
  const classes = useStyles()
  const hasReminders = reminders.length > 0

  const createNewCalendarEventReminder = async () => {
    const guessedReminderTime = guessReminderTime(refStartDate, refEndDate, "calendarEvent")
    const reminderData: Omit<BaseNewReminderData, "triggerType"> = {
      entityId: calendarEvent.id,
      entityType: "calendarEvent",
      recipients: {
        roles: ["calendarEventParticipants"],
        emails: [],
      },
    }

    const reminderPromise = isDate(guessedReminderTime)
      ? createReminder({
          ...reminderData,
          reminderTime: guessedReminderTime.toISOString(),
          reminderTimeDetails: undefined,
          triggerType: "absoluteTime",
        })
      : createReminder({
          ...reminderData,
          reminderTime: undefined,
          reminderTimeDetails: guessedReminderTime,
          triggerType: "relativeTime",
        })

    return (await reminderPromise) as ReminderViewModel
  }

  return (
    <Box className={classes.container} style={{ borderBottom: hasReminders ? undefined : "0 !important" }}>
      <Accordion className={classes.accordion}>
        <AccordionSummary expandIcon={<ChevronDown color="black" />}>
          <Typography variant="body1">
            <strong>{translations.notifications}</strong>
            {hasReminders && (
              <Typography variant="subtitle1" component="span" className={classes.notificationCount}>
                {reminders.length > 9 ? "9+" : reminders.length}
              </Typography>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {isLoading && <CircularProgress />}
          {!isLoading && (
            <ReminderList
              reminders={reminders}
              refStartDate={refStartDate}
              refEndDate={refEndDate}
              onCreateReminder={createNewCalendarEventReminder}
              canCreateReminder={calendarEvent.canUpdate}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  accordion: {
    boxShadow: "none",
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&.Mui-expanded": {
      borderBottom: 0,
    },
    "& .MuiAccordionSummary-root": {
      padding: 0,
      fontWeight: "bold",
    },
    "& .MuiAccordionDetails-root": {
      padding: 0,
    },
  },
  notificationCount: {
    marginLeft: theme.spacing(1),
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: `1px 8px`,
    borderRadius: theme.spacing(2),
    fontWeight: 700,
    letterSpacing: 0.5,
    fontSize: "0.75rem",
  },
}))

const defaultTranslations = {
  notifications: "Notifications",
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { t } = useI18n("translation")

  return {
    notifications: t("notifications", defaults.notifications),
  }
}

export default CalendarEventReminders

type CalendarEventRemindersProps = {
  calendarEvent: CalendarEventViewModel
}
type Translations = typeof defaultTranslations
